import type { Visibility } from './types'

export interface DocSource {
  service: string
  /** Full slash path, e.g. `doctor-desk/exams`. Missing levels are created. */
  topic: string
  title: string
  description: string
  visibility: Visibility
  tags: string[]
  markdown: string
}

/** YAML needs quoting for anything that could be read as punctuation or a number. */
const yamlString = (s: string) => `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`

/**
 * Serialise to the same file the IDE machine uploads: YAML frontmatter followed by
 * the body. Authoring in the browser has to produce a byte-identical shape,
 * otherwise a page written here renders differently from one written in an editor.
 *
 * `extra` appends keys the authoring contract does not know about — provenance for
 * a file that will be read outside the wiki. `fromFrontmatter` skips keys it does
 * not recognise, so carrying them costs the round trip nothing.
 */
export function toFrontmatter(doc: DocSource, extra?: Record<string, string>): string {
  const lines = [
    '---',
    `service: ${doc.service}`,
    `topic: ${doc.topic}`,
    `title: ${yamlString(doc.title)}`,
  ]
  if (doc.description) lines.push(`description: ${yamlString(doc.description)}`)
  lines.push(`visibility: ${doc.visibility}`)
  if (doc.tags.length) lines.push(`tags: [${doc.tags.join(', ')}]`)
  for (const [key, value] of Object.entries(extra ?? {})) {
    lines.push(`${key}: ${yamlString(value)}`)
  }
  lines.push('---', '', doc.markdown.trim(), '')

  return lines.join('\n')
}

/** Parse a doc file back into fields, so an existing page can be reopened for editing. */
export function fromFrontmatter(raw: string): Partial<DocSource> & { markdown: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/)
  if (!match) return { markdown: raw }

  const [, head = '', body = ''] = match
  const out: Partial<DocSource> & { markdown: string } = { markdown: body.trim() }

  for (const line of head.split(/\r?\n/)) {
    const kv = line.match(/^(\w+):\s*(.*)$/)
    if (!kv) continue
    const [, key, value = ''] = kv
    const unquoted = value.replace(/^"(.*)"$/, '$1').replace(/\\"/g, '"')

    if (key === 'tags') {
      out.tags = value
        .replace(/^\[|\]$/g, '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
    } else if (key === 'visibility') {
      out.visibility = unquoted === 'internal' ? 'internal' : 'public'
    } else if (key === 'service' || key === 'topic' || key === 'title' || key === 'description') {
      out[key] = unquoted
    }
  }
  return out
}
